// The village's next goal, long-term choices, and reasons to leave the doorstep.
import {
  IMPROVEMENTS, MAX_IMPROVEMENT, MILESTONES,
  improvementCost, realmRank, type Improvement,
} from '../../shared/realm'
import type { ClientMsg } from '../../shared/protocol'
import type { Stockpile } from '../../shared/types'
import { VILLAGE_FOCUS, type VillageFocus } from '../../shared/governor'
import { el, type UICtx } from './panels'
import { campProjectCard, renderCampLauncher, villageFoodCard, wildlifeTime } from './campProject'
import { councilArt, resourceChips, type CouncilArtKind } from './councilArt'

type CouncilTab = 'village' | 'goals' | 'improve'
const TAB_INFO: { key: CouncilTab; label: string; art: CouncilArtKind; hint: string }[] = [
  { key: 'village', label: 'Village', art: 'settlement', hint: 'Plan & manage' },
  { key: 'goals', label: 'Milestones', art: 'people', hint: 'Earn rewards' },
  { key: 'improve', label: 'Upgrades', art: 'workshop', hint: 'Lasting benefits' },
]
const MILESTONE_ART: Record<string, CouncilArtKind> = {
  shelter: 'house', table: 'farm', tools: 'tools', crew: 'people', stores: 'storage',
  harvest: 'farm', watch: 'watch', forge: 'smithy', town: 'timberhall',
}
const MILESTONE_TASK: Record<string, string> = {
  shelter: 'Build 1 house', table: 'Build 1 farm or fishing pier', tools: 'Craft 1 tool',
  crew: 'Put 2 villagers to work', stores: 'Build 1 storehouse', harvest: 'Harvest 30 food',
  watch: 'Equip 3 armed villagers', forge: 'Build 1 smithy', town: 'Reach 20 villagers',
}
const UPGRADE_ART: Record<Improvement, CouncilArtKind> = { harvest: 'farm', builders: 'house', craft: 'workshop' }
const UPGRADE_BENEFIT: Record<Improvement, { step: number; label: string }> = {
  harvest: { step: 20, label: 'farm & fishing output' }, builders: { step: 20, label: 'construction speed' },
  craft: { step: 25, label: 'crafting speed' },
}

function renownChip(amount: number, available?: number): HTMLElement {
  const chip = el('span', 'council-renown' + (available !== undefined && available < amount ? ' short' : ''))
  const icon = el('span', '', '✦'); icon.setAttribute('aria-hidden', 'true')
  chip.append(icon, el('strong', '', String(amount)), el('span', '', 'Renown'))
  chip.title = available === undefined ? `${amount} renown` : `${amount} renown needed · ${available} available`
  return chip
}
function sectionHeading(title: string, hint: string, aside?: HTMLElement): HTMLElement {
  const head = el('div', 'council-section-head'), copy = el('div')
  copy.append(el('h3', '', title), el('p', '', hint)); head.append(copy)
  if (aside) head.append(aside)
  return head
}

export function initJournal({ st, tx, root, jumpTo }: UICtx): void {
  const launch = el('button', 'panel journal-launch hidden')
  launch.setAttribute('aria-label', 'Open Village Council (J)')
  launch.setAttribute('aria-haspopup', 'dialog'); launch.setAttribute('aria-expanded', 'false')
  const back = el('div', 'modal-back journal-back hidden')
  const panel = el('section', 'panel journal council-visual')
  panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true')
  panel.setAttribute('aria-labelledby', 'journal-title')
  const head = el('div', 'journal-head'), heading = el('div')
  heading.append(el('span', 'council-eyebrow', 'YOUR SETTLEMENT'))
  const title = el('h2', '', 'Village Council'); title.id = 'journal-title'
  heading.append(title)
  const close = el('button', 'btn btn-ghost', 'Close ×'); close.setAttribute('aria-label', 'Close council')
  head.append(heading, close)
  const summary = el('div', 'journal-summary')
  const tabs = el('div', 'journal-tabs'); tabs.setAttribute('role', 'tablist'); tabs.setAttribute('aria-label', 'Council menus')
  const error = el('div', 'journal-error hidden'); error.setAttribute('role', 'alert')
  const body = el('div', 'journal-body'); body.id = 'council-content'; body.setAttribute('role', 'tabpanel')
  panel.append(head, summary, tabs, error, body); back.append(panel); root.append(launch, back)
  let open = false, active: CouncilTab = 'village', signature = '', illustratedTabs = false
  const detailState = new Map<string, boolean>()
  function setOpen(value: boolean): void {
    open = value; back.classList.toggle('hidden', !value)
    launch.setAttribute('aria-expanded', String(value))
    if (open) { signature = ''; refresh(); close.focus() } else launch.focus()
  }
  function selectTab(key: CouncilTab): void {
    if (active === key) return
    active = key; signature = ''; body.scrollTop = 0; refresh()
  }
  launch.onclick = () => setOpen(!open); close.onclick = () => setOpen(false)
  st.on('openCouncil', () => { active = 'village'; body.scrollTop = 0; setOpen(true) })
  back.onclick = e => { if (e.target === back) setOpen(false) }
  for (const { key, label, hint } of TAB_INFO) {
    const b = el('button', 'btn council-tab'), words = el('span', 'council-tab-copy')
    words.append(el('strong', '', label), el('span', '', hint)); b.append(words)
    b.dataset.tab = key; b.id = `council-tab-${key}`
    b.setAttribute('role', 'tab'); b.setAttribute('aria-controls', body.id)
    b.onclick = () => selectTab(key)
    tabs.append(b)
  }
  function affordable(cost: Partial<Stockpile>): boolean {
    return Object.entries(cost).every(([r, n]) => (st.self?.stockpile[r as keyof Stockpile] ?? 0) + 1e-9 >= n!)
  }
  function action(label: string, key: string, message: ClientMsg, disabled = false): HTMLButtonElement {
    const b = el('button', 'btn btn-gold journal-action', label); b.dataset.action = key
    b.disabled = disabled || !st.connected
    b.onclick = () => {
      error.classList.add('hidden'); b.disabled = true
      close.focus({ preventScroll: true }); tx(message)
    }
    return b
  }
  function illustratedCard(kind: CouncilArtKind, title: string, className: string): { item: HTMLElement; copy: HTMLElement } {
    const item = el('article', `journal-card ${className}`), copy = el('div', 'council-card-copy')
    copy.append(el('h3', '', title)); item.append(councilArt(kind, 'council-card-art'), copy)
    return { item, copy }
  }
  function timer(until: number, prefix: string): HTMLElement {
    const label = el('span', 'journal-timer'); label.dataset.until = String(until); label.dataset.prefix = prefix
    return label
  }
  function updateTimers(): void {
    for (const e of panel.querySelectorAll<HTMLElement>('[data-until]')) {
      const seconds = Math.max(0, Math.ceil(Number(e.dataset.until) - st.tick))
      e.textContent = `${e.dataset.prefix} ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
    }
    for (const e of panel.querySelectorAll<HTMLElement>('[data-wildlife-until]')) {
      const remaining = wildlifeTime(Number(e.dataset.wildlifeUntil), st.tick)
      e.textContent = remaining ? `${remaining} left` : 'Protection ended'
    }
  }
  function refresh(): void {
    const p = st.self?.realm
    const visible = !!p && !!st.me && !st.observer && !st.self?.ruined
    launch.classList.toggle('hidden', !visible)
    if (!visible || !p) { if (open) setOpen(false); return }
    const rank = realmRank(p.earned), self = st.self!
    const governor = self.governor, automatic = governor?.enabled !== false
    const camp = self.camp, food = self.food
    if (camp) renderCampLauncher(launch, camp, st.tick)
    else {
      launch.classList.remove('camp-launch')
      launch.setAttribute('aria-label', 'Open Village Council (J)')
      launch.replaceChildren(el('span', 'journal-eyebrow', `✦ ${rank.name} · ${p.renown} renown`),
        el('strong', '', automatic ? VILLAGE_FOCUS[governor?.focus ?? 'balanced'].name : 'Manual village management'),
        el('span', 'journal-launch-hint', 'Village Council · J'))
    }
    if (!open) return
    if (!illustratedTabs) {
      for (const [i, tab] of TAB_INFO.entries()) tabs.children[i].prepend(councilArt(tab.art, 'council-tab-art'))
      illustratedTabs = true
    }
    const common = [active, p.renown, p.earned, self.pop, self.popCap, Object.values(self.stockpile).map(Math.floor), self.stockpile.food > 0, st.connected]
    const key = JSON.stringify([...common, active === 'village'
      ? [governor, camp && { ...camp, wood: Math.floor(camp.wood), storageFree: Math.floor(camp.storageFree), storageNeeded: Math.ceil(camp.storageNeeded) }, food]
      : active === 'goals' ? [p.claimed, Object.entries(p.progress).map(([k, n]) => [k, Math.floor(n)]), automatic]
        : [p.improvements, p.nextFestival <= st.tick, p.festivalUntil > st.tick]])
    if (key === signature) { updateTimers(); return }
    signature = key
    const scroll = body.scrollTop
    const focused = panel.contains(document.activeElement) ? (document.activeElement as HTMLElement).dataset.action : undefined
    for (const d of body.querySelectorAll<HTMLDetailsElement>('details')) detailState.set(d.className, d.open)
    for (const b of tabs.children) {
      const selected = (b as HTMLElement).dataset.tab === active
      b.setAttribute('aria-selected', String(selected)); b.setAttribute('aria-pressed', String(selected))
      ;(b as HTMLElement).tabIndex = selected ? 0 : -1
    }
    body.setAttribute('aria-labelledby', `council-tab-${active}`)
    const rankBlock = el('div', 'council-rank-block')
    rankBlock.append(el('span', 'journal-rank', rank.name))
    const rankTrack = el('progress', 'council-rank-meter')
    rankTrack.max = rank.next ?? Math.max(500, p.earned); rankTrack.value = p.earned
    rankTrack.setAttribute('aria-label', 'Village rank progress')
    rankBlock.append(rankTrack, el('span', '', rank.next ? `${p.earned} / ${rank.next} → ${realmRank(rank.next).name}` : 'Highest village rank'))
    const purse = el('div', 'council-purse'); purse.append(renownChip(p.renown), el('span', '', 'Spend on upgrades'))
    summary.replaceChildren(rankBlock, purse)
    body.replaceChildren(); body.dataset.view = active
    if (active === 'village') {
      const overview = el('div', 'council-overview')
      if (camp) overview.append(campProjectCard(camp, st.tick, action, find => { jumpTo(find.q, find.r); setOpen(false) }))
      if (food) overview.append(villageFoodCard(food, self.pop, action, self.stockpile.food, self.popCap))
      body.append(overview)
      const priority = el('article', 'journal-card council-priority')
      priority.append(sectionHeading('What should we focus on?', 'Choose a priority. Your villagers handle the jobs.', el('span', `council-status-pill ${automatic ? 'good' : ''}`, automatic ? 'Automatic crews' : 'Manual crews')))
      const choices = el('div', 'village-policies council-focus-cards')
      const focusArt: Record<VillageFocus, CouncilArtKind> = { balanced: 'balanced', food: 'farm', industry: 'industry' }
      const names = { balanced: 'Balanced', food: 'Food first', industry: 'Industry' }
      const hints = { balanced: 'A little of everything', food: 'Fill farms & fishing piers', industry: 'Prioritize stone & ore' }
      for (const focus of Object.keys(VILLAGE_FOCUS) as VillageFocus[]) {
        const selected = automatic && (governor?.focus ?? 'balanced') === focus
        const button = action(names[focus], `focus-${focus}`, { t: 'governor', enabled: true, focus }, selected)
        const text = el('span', 'council-focus-copy')
        text.append(el('strong', '', names[focus]), el('span', '', hints[focus]))
        const check = el('span', 'council-choice-check', selected ? '✓' : ''); check.setAttribute('aria-hidden', 'true')
        button.replaceChildren(councilArt(focusArt[focus]), text, check)
        button.setAttribute('aria-label', names[focus]); button.setAttribute('aria-pressed', String(selected)); button.title = VILLAGE_FOCUS[focus].desc
        choices.append(button)
      }
      priority.append(choices, el('p', 'council-caption', governor?.status ?? 'Villagers are organizing their work.'))
      const controls = el('details', 'council-management'), toggle = el('summary', '', 'Crew settings')
      controls.append(toggle, el('p', '', 'Automatic crews share staffing, tools and logging. Food shortages come first; paused buildings and manual crews stay under your control.'))
      const buttons = el('div', 'council-management-actions')
      buttons.append(action(automatic ? 'Switch to manual management' : 'Enable automatic management', 'governor-toggle', { t: 'governor', enabled: !automatic, focus: governor?.focus ?? 'balanced' }),
        action('Automate all workplaces', 'governor-all', { t: 'governor', enabled: true, focus: governor?.focus ?? 'balanced', takeOver: true }))
      controls.append(buttons, el('p', 'council-caption', 'Automate all workplaces also replaces your manually assigned crews.'))
      priority.append(controls); body.append(priority)
    } else if (active === 'goals') {
      const earned = MILESTONES.filter(g => p.claimed.includes(g.id)).length
      body.append(sectionHeading('Every step builds your village', automatic ? 'Rewards are collected automatically as you finish milestones.' : 'Complete a milestone, then claim its reward.', el('span', 'council-status-pill', `${earned} / ${MILESTONES.length} earned`)))
      const current = el('div', 'council-milestone-grid'), completed = el('details', 'council-earned')
      const earnedGrid = el('div', 'council-milestone-grid')
      completed.append(el('summary', '', `Completed milestones · ${earned}`), earnedGrid)
      for (const g of MILESTONES) {
        const done = p.claimed.includes(g.id), n = done ? g.goal : Math.max(0, Math.floor(p.progress[g.id] ?? 0)), complete = n >= g.goal
        const { item, copy } = illustratedCard(MILESTONE_ART[g.id], g.title, 'council-milestone')
        item.classList.toggle('completed', done); item.classList.toggle('claimable', complete && !done)
        copy.append(el('p', 'council-task', MILESTONE_TASK[g.id]))
        copy.title = g.hint
        const meterRow = el('div', 'council-milestone-progress')
        const progress = el('progress', 'council-meter'); progress.max = g.goal; progress.value = Math.min(g.goal, n)
        progress.setAttribute('aria-label', g.title)
        meterRow.append(progress, el('strong', '', done ? '✓' : `${Math.min(n, g.goal)} / ${g.goal}`))
        const reward = el('div', 'council-reward-line'); reward.append(renownChip(g.reward.renown), resourceChips(g.reward.goods))
        copy.append(meterRow, reward)
        if (done) copy.append(el('span', 'journal-done', '✓ Reward earned'))
        else if (!automatic) copy.append(action(complete ? 'Claim reward' : 'In progress', g.id, { t: 'milestone', id: g.id }, !complete))
        else if (complete) copy.append(el('span', 'journal-done', 'Collecting reward…'))
        ;(done ? earnedGrid : current).append(item)
      }
      if (current.childElementCount) body.append(current)
      else body.append(el('p', 'council-all-earned', '✓ Every milestone earned. Your village has come a long way.'))
      if (earnedGrid.childElementCount) body.append(completed)
    } else if (active === 'improve') {
      body.append(sectionHeading('Make your village better', 'Permanent benefits. Choose where to spend your renown.'))
      const grid = el('div', 'council-upgrade-grid')
      for (const id of Object.keys(IMPROVEMENTS) as Improvement[]) {
        const def = IMPROVEMENTS[id], level = p.improvements[id] ?? 0, cost = improvementCost(id, level), benefit = UPGRADE_BENEFIT[id]
        const max = level >= MAX_IMPROVEMENT
        const { item, copy } = illustratedCard(UPGRADE_ART[id], def.name, 'council-upgrade')
        const effects = el('div', 'council-benefit-value')
        effects.append(el('strong', '', `+${benefit.step * (max ? level : level + 1)}%`), el('span', '', benefit.label))
        copy.append(el('span', 'council-benefit-caption', max ? 'Current benefit' : 'After upgrade'), effects)
        const levels = el('div', 'council-levels'); levels.setAttribute('aria-label', `Level ${level} of ${MAX_IMPROVEMENT}`)
        for (let i = 1; i <= MAX_IMPROVEMENT; i++) levels.append(el('span', i <= level ? 'earned' : i === level + 1 ? 'next' : '', String(i)))
        levels.append(el('small', '', max ? 'Fully upgraded' : `Level ${level} → ${level + 1}`)); copy.append(levels)
        const footer = el('div', 'council-upgrade-footer')
        if (!max) {
          const costRow = el('div', 'council-reward-line'); costRow.append(renownChip(cost.renown, p.renown), resourceChips(cost.goods, self.stockpile)); footer.append(costRow)
        }
        const button = action(max ? 'Mastered ✓' : p.renown < cost.renown ? 'Need renown' : !affordable(cost.goods) ? 'Need supplies' : 'Upgrade', id, { t: 'improve', id }, max || p.renown < cost.renown || !affordable(cost.goods))
        button.setAttribute('aria-label', max ? `${def.name} mastered` : `Improve ${def.name} to level ${level + 1}`)
        footer.append(button); item.append(footer); grid.append(item)
      }
      body.append(grid)
      const { item: feast, copy } = illustratedCard('people', 'A village festival', 'council-festival')
      const effects = el('div', 'camp-benefits'); effects.append(el('span', '', '+25% harvest · 5 min'), el('span', '', '+25 hunger points'))
      copy.append(effects)
      if (p.festivalUntil > st.tick) copy.append(timer(p.festivalUntil, 'Celebration ends in'))
      else if (p.nextFestival > st.tick) copy.append(timer(p.nextFestival, 'Ready again in'))
      else copy.append(el('p', 'council-caption', self.pop < 10 ? `${self.pop} / 10 villagers needed to celebrate` : 'Your village is ready to celebrate.'))
      const feastAction = el('div', 'council-festival-action'); feastAction.append(resourceChips({ food: 30, gold: 5 }, self.stockpile), action('Hold a festival', 'festival', { t: 'festival' }, p.nextFestival > st.tick || self.pop < 10 || !affordable({ food: 30, gold: 5 })))
      feast.append(feastAction); body.append(feast)
    }
    for (const d of body.querySelectorAll<HTMLDetailsElement>('details')) {
      d.open = detailState.get(d.className) ?? false
      const summary = d.querySelector('summary'); if (summary) summary.dataset.action = `details-${d.className}`
    }
    body.scrollTop = scroll
    if (focused) ([...panel.querySelectorAll<HTMLElement>('[data-action]')].find(b => b.dataset.action === focused && !(b as HTMLButtonElement).disabled) ?? close).focus({ preventScroll: true })
    updateTimers()
  }
  window.addEventListener('keydown', e => {
    const target = e.target as HTMLElement
    if (target.matches('input,textarea,select') || target.isContentEditable) return
    if (e.key.toLowerCase() === 'j' && !launch.classList.contains('hidden')) {
      e.preventDefault(); e.stopImmediatePropagation(); setOpen(!open)
    } else if (open && e.key === 'Escape') {
      e.preventDefault(); e.stopImmediatePropagation(); setOpen(false)
    } else if (open && tabs.contains(target) && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      e.preventDefault(); e.stopImmediatePropagation()
      const index = TAB_INFO.findIndex(tab => tab.key === active)
      const next = e.key === 'Home' ? 0 : e.key === 'End' ? TAB_INFO.length - 1
        : (index + (e.key === 'ArrowRight' ? 1 : -1) + TAB_INFO.length) % TAB_INFO.length
      selectTab(TAB_INFO[next].key); (tabs.children[next] as HTMLButtonElement).focus()
    } else if (open && e.key === 'Tab') {
      const controls = [...panel.querySelectorAll<HTMLElement>('button:not(:disabled):not([tabindex="-1"]),a[href],summary')].filter(e => e.getClientRects().length > 0)
      const first = controls[0], last = controls[controls.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
    } else if (open) e.stopPropagation()
  }, true)
  for (const event of ['self', 'welcome', 'predict', 'selection', 'tick', 'connection']) st.on(event, refresh)
  st.on('error', msg => { if (open) { signature = ''; refresh(); error.textContent = String(msg); error.classList.remove('hidden') } })
}
